import { useCallback, useRef } from 'react'
import type { CenterCanvasConfig } from '../types/center-canvas-config'

/**
 * Latest-value mirror of the `linkedTextChains` prop, plus the stable getter ShapeManager reads
 * the chains through.
 */
export function useLinkedChainsRef({
  linkedTextChains,
}: {
  linkedTextChains: CenterCanvasConfig['linkedTextChains']
}) {
  // Same reason as `unifiedLayerIdsRef` (`use-layer-arrange-gates`): ShapeManager reads the chains
  // through a stable getter when deciding which text boxes a group may adopt (WMPCM-6948), so the
  // prop is mirrored rather than closed over — otherwise every chain edit would rebuild that callback.
  //
  // Assigned DURING RENDER, never in an effect (WMPCM-6867). ShapeManager reads this getter in its
  // own render body — `liveChains`, `hasOutgoingLink`/`hasIncomingLink` per frame, and the
  // `onSelectChain` gate — as do the canvas's own render-time reads (the object context menu's Cut
  // gate, in `components/canvas-context-menus.tsx`). Effects run after commit and a ref write
  // schedules no re-render, so an effect-time sync made the very commit that carried a new chain
  // paint the PRE-link one, with nothing left to correct it: the source frame kept its overflow
  // badge (`showsOverflowBadge` is gated on `!hasNextInChain`), kept the solo-frame word policy,
  // and reported no chain to select across. Idempotent assignment of the current prop, so a
  // StrictMode double-render or a concurrent re-render writes the same value — the same pattern
  // `home.tsx` uses for `textChainsRef` and `shape-manager/hooks/use-shape-text-wrap-context.ts`
  // for `shapesForWrapRef`.
  const linkedTextChainsRef = useRef(linkedTextChains)

  linkedTextChainsRef.current = linkedTextChains

  const getLinkedTextChainsForShapeManager = useCallback(
    () => linkedTextChainsRef.current ?? [],
    []
  )

  return { linkedTextChainsRef, getLinkedTextChainsForShapeManager }
}
